<template>
    <div class="columns">
        <div class="column main-column">
            <div class="add-episode-details-tab">
                <form-row
                    id="title"
                    label="Episode Title"
                    :error="episodeModal.getError('title')"
                >
                    <form-input
                        id="title"
                        v-model="episode.title"
                        type="text"
                        autocomplete="off"
                        :error="episodeModal.hasError('title')"
                        @change="updateEpisode"
                        @focus="episodeModal.clearError('title')"
                    />
                </form-row>

                <form-row
                    id="description"
                    class="description"
                    label="Episode Description"
                    :help="descriptionRemainingCharactersHelp"
                    :error="episodeModal.getError('description')"
                >
                    <form-rich-editor
                        id="description"
                        v-model="episode.description"
                        name="description"
                        :error="episodeModal.hasError('description')"
                        @input="updateEpisode"
                        @focus="episodeModal.clearError('description')"
                    />
                </form-row>

                <form-row
                    id="summary"
                    label="Episode Summary"
                    :error="episodeModal.getError('summary')"
                >
                    <form-input
                        id="summary"
                        v-model="episode.summary"
                        type="text"
                        autocomplete="off"
                        :error="episodeModal.hasError('summary')"
                        @change="updateEpisode"
                        @focus="episodeModal.clearError('summary')"
                    />
                </form-row>

                <form-row
                    id="artist-guest"
                    label="Artist / Guest"
                    :error="episodeModal.getError('artistGuest')"
                >
                    <form-input
                        id="artist-guest"
                        v-model="episode.artistGuest"
                        type="text"
                        autocomplete="off"
                        :error="episodeModal.hasError('artistGuest')"
                        @change="updateEpisode"
                        @focus="episodeModal.clearError('artistGuest')"
                    />
                </form-row>

                <form-row
                    id="tags"
                    label="Tags"
                    help="(Separated by comma)"
                    :error="episodeModal.getError('tags')"
                >
                    <form-input
                        id="tags"
                        v-model="episode.tags"
                        type="text"
                        autocomplete="off"
                        :error="episodeModal.hasError('tags')"
                        @change="updateEpisode"
                        @focus="episodeModal.clearError('tags')"
                    />
                </form-row>

                <div class="columns">
                    <div
                        v-if="'serial' === show.podcastType"
                        class="column"
                    >
                        <form-row
                            id="season-no"
                            label="Season #"
                            help="(1, 2, etc.)"
                            :error="episodeModal.getError('seasonNo')"
                        >
                            <form-input
                                id="season-no"
                                v-model.number="episode.seasonNo"
                                type="number"
                                autocomplete="off"
                                :error="episodeModal.hasError('seasonNo')"
                                @change="updateEpisode"
                                @focus="episodeModal.clearError('seasonNo')"
                            />
                        </form-row>
                    </div>

                    <div class="column">
                        <form-row
                            id="episode-no"
                            label="Episode #"
                            help="(1, 2, etc.)"
                            :error="episodeModal.getError('episodeNo')"
                        >
                            <form-input
                                id="episode-no"
                                v-model.number="episode.episodeNo"
                                type="number"
                                autocomplete="off"
                                :error="episodeModal.hasError('episodeNo')"
                                @change="updateEpisode"
                                @focus="episodeModal.clearError('episodeNo')"
                            />
                        </form-row>
                    </div>

                    <div class="column">
                        <form-row
                            id="episode-type"
                            label="Episode Type"
                            :error="episodeModal.getError('type')"
                        >
                            <form-select
                                id="episode-type"
                                v-model="episode.type"
                                :options="episodeTypes"
                                :error="episodeModal.hasError('type')"
                                @change="updateEpisode"
                                @focus="episodeModal.clearError('type')"
                            />
                        </form-row>
                    </div>
                </div>

                <div class="explicit-content">
                    <form-row
                        :error="episodeModal.getError('explicitContent')"
                    >
                        <p class="title">
                            Explicit Content?
                        </p>
                        <base-checkbox
                            :values="episode.explicitContent || false"
                            :value="episode.explicitContent"
                            label="This episode contains explicit material."
                            @change="onExplicitContentChange"
                        />
                    </form-row>
                </div>
            </div>
        </div>

        <div class="column side-column">
            <audio-file-box
                @upload="file => $emit('upload', file)"
                @cancel="$emit('cancel')"
            />

            <artwork-file-box
                :default-artwork="show.cover.imageUrl"
            />
        </div>
    </div>
</template>
<script>
import { mapMutations, mapState } from 'vuex';
import ArtworkFileBox from '@/components/Shows/Episodes/EpisodeModal/ArtworkFileBox';
import AudioFileBox from '@/components/Shows/Episodes/EpisodeModal/AudioFileBox';
import episodeModal from '@/utils/episodeModal';
import FormInput from '@/components/Form/FormInput';
import FormRichEditor from '@/components/Form/FormRichEditor';
import FormRow from '@/components/Form/FormRow';
import FormSelect from '@/components/Form/FormSelect';
import { defaultAttributes, episodeTypes, descriptionCharactersLimit } from '@/utils/config/episodeOptions';
import htmlToText from '@/utils/htmlToText';

export default {
    components: {
        ArtworkFileBox,
        AudioFileBox,
        FormInput,
        FormRichEditor,
        FormRow,
        FormSelect,
    },

    props: {
        show: {
            type: [Object, Boolean],
            default: () => ({}),
        },
    },

    data: () => ({
        episodeModal,
        descriptionCharactersLimit,
        episodeTypes,
    }),

    computed: {
        ...mapState(['UIState']),
        ...mapState({
            addEpisodeModal: state => state.UIState.addEpisodeModal,
            context: state => state.UIState.addEpisodeModal.context,
            episode: state => state.UIState.addEpisodeModal.episode || {},
        }),

        /**
         * Computes amount of remaining characters in description.
         *
         * @returns {number} Amount of remaining characters.
         */
        descriptionRemainingCharacters() {
            return this.descriptionCharactersLimit - (htmlToText(this.episode.description || '')).length;
        },

        /**
         * Computes help text for description remaining characters.
         *
         * @returns {string} Description remainig charaters help text.
         */
        descriptionRemainingCharactersHelp() {
            if (0 <= this.descriptionRemainingCharacters) {
                return `(${this.descriptionRemainingCharacters} character(s) remaining)`;
            }

            return `(over ${-this.descriptionRemainingCharacters} characters)`;
        },
    },

    /**
     * Fills episode state with default values.
     */
    async mounted() {
        this.updateState('episode', {
            ...defaultAttributes,
            ...{
                artistGuest: null,
                artwork: null,
                description: null,
                episodeNo: null,
                explicitContent: false,
                publishedAt: null,
                seasonNo: null,
                summary: null,
                tags: null,
                title: '',
            },
            ...this.episode,
        });
    },

    methods: {
        ...mapMutations(['changeUIState']),

        /**
         * Handles change event on explicit content checkbox.
         *
         * @param  {boolean} value Checkbox state value.
         */
        onExplicitContentChange(value) {
            this.episode.explicitContent = value;

            this.updateEpisode();
        },

        /**
         * Updates episode in state.
         */
        updateEpisode() {
            this.updateState('episode', this.episode);
        },

        /**
         * Updates Episode Modal state on given key with given data.
         *
         * @param  {string} key  State field key.
         * @param  {object} data Data to update.
         */
        updateState(key, data) {
            this.changeUIState({
                addEpisodeModal: {
                    ...this.UIState.addEpisodeModal,
                    [key]: {
                        ...this.UIState.addEpisodeModal[key],
                        ...data,
                    },
                },
            });
        },
    },
};
</script>
